import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '@hooks/dark-mode';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CallToAction: React.FC<{ reversed?: boolean }> = ({ reversed = false }) => {
    const { dark: isDarkTheme } = useDarkMode();
    const { t } = useTranslation();

    // Dynamic colors based on theme
    const titleColor = isDarkTheme ? "#ffffff" : "#111827";
    const subtitleColor = isDarkTheme ? "#d1d5db" : "#6b7280";
    const accentColor = isDarkTheme ? "#80ABF9" : "#1673FF";

    const gradient = reversed
        ? (isDarkTheme
            ? 'linear-gradient(to bottom, #1C1C1E 0%, #1C1C1E 40%, rgba(28,28,30,0.6) 60%, rgba(28,28,30,0.15) 85%, transparent 100%)'
            : 'linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 40%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0.15) 85%, transparent 100%)')
        : (isDarkTheme
            ? 'linear-gradient(to bottom, #28282B 0%, #28282B 40%, rgba(40, 40, 43, 0.6) 60%, rgba(40, 40, 43, 0.15) 85%, transparent 100%)'
            : 'linear-gradient(to bottom, #F0F0F0 0%, #F0F0F0 40%, rgba(240, 240, 240, 0.6) 60%, rgba(240, 240, 240, 0.15) 85%, transparent 100%)');


    return (
        <section
            className={`relative py-20 overflow-hidden ${reversed ? 'bg-bg' : 'bg-secBg'}`}
        >
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden">
                <div
                    aria-hidden
                    className="absolute top-0 left-0 right-0"
                    style={{
                        pointerEvents: "none",
                        height: "100px",
                        background: gradient
                    }}
                />

                {/* Note: floating blobs removed in favor of conic + sweep animations */}
            </div>

            <div className="relative z-10 max-w-screen-xl mx-auto px-4">
                <div className="flex items-center justify-center gap-6 w-full">
                    {/* Left side - Content */}
                    <div className="text-center">
                        {/* Main Title */}
                        <motion.h2
                            className="text-3xl md:text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight mt-10 mb-3"
                            style={{ color: titleColor }}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            {t('home.cta.titleStart')} {" "}
                            <span
                                className="transition-colors duration-300"
                                style={{ color: accentColor }}
                            >
                                {t('home.cta.titleAccent')}
                            </span>
                        </motion.h2>

                        {/* Subtitle */}
                        <motion.p
                            className="text-lg md:text-xl font-normal mb-4"
                            style={{ color: subtitleColor }}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            {t('home.cta.subtitle')}
                        </motion.p>

                        {/* Contact Information */}
                        <motion.div
                            className="mb-6 space-y-3 flex flex-col items-center"
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <div className="flex items-center justify-center gap-3">
                                <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                <a
                                    href="mailto:info@drivn3.com"
                                    className="text-lg font-medium hover:underline transition-colors duration-200"
                                    style={{ color: accentColor }}
                                >
                                    info@drivn3.com
                                </a>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                                <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                <a
                                    href="tel:+4560826116"
                                    className="text-lg font-medium hover:underline transition-colors duration-200"
                                    style={{ color: accentColor }}
                                >
                                    +45 60 82 61 16
                                </a>
                            </div>
                        </motion.div>

                        {/* CTA Buttons */}
                        <motion.div
                            className="flex flex-col sm:flex-row gap-4 justify-center"
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            {/* Primary CTA Button */}
                            <motion.a
                                href="mailto:info@drivn3.com"
                                className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white w-full sm:w-auto rounded-xl transition-all duration-200 shadow-lg"
                                style={{
                                    backgroundColor: accentColor,
                                    textDecoration: 'none'
                                }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                            >
                                {t('home.cta.emailButton')}
                                <svg
                                    className="ml-2 w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                            </motion.a>

                            {/* Secondary CTA Button */}
                            <motion.div
                                className="w-full sm:w-auto"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                            >
                                <Link
                                    to="/contact-sales"
                                    className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white w-full sm:w-auto rounded-xl transition-all duration-200 shadow-lg"
                                    style={{
                                        backgroundColor: accentColor,
                                        textDecoration: 'none'
                                    }}
                                >
                                    {t('home.cta.bookMeeting')}
                                    <svg
                                        className="ml-2 w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </Link>
                            </motion.div>
                        </motion.div>
                    </div>


                </div>
            </div>
        </section>
    );
};

export default CallToAction; 